import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { TaxDocumentUploadModal } from "../../components/admin/TaxDocumentUploadModal";
import { useUploadTaxDocument } from "../../hooks/useTaxDocumentMutations";
import { ApiError } from "../../lib/apiClient";
import type {
  TaxDocumentUploadFormValues,
  TaxDocumentUploadResponse,
} from "../../schemas/taxDocumentUpload";

/**
 * UC-008 (Phase 22) — Prefakturace A&L soft admin detail.
 *
 * Per spec/use-cases/UC-008 + spec/docs/04-architecture/file-storage.md rev 4:
 *
 *   Sekce 1: "Daňový doklad — distribuce" (status badge + Upload button)
 *   Sekce 2: Upload Modal (10-step validation + ClamAV + auto-attach)
 *
 * R-033 guard: pokud rozpočet není schválen, "Nahrát doklad" je disabled +
 * inline Alert v modalu (princip #2 SoT — žádný doklad bez schváleného Budget).
 *
 * Po success: inline Alert s attachments_count + automatický email Davidovi (server).
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/prefakturace-alsoft/$monthId",
  component: Prefakturace,
});

function Prefakturace() {
  const { monthId } = Route.useParams();
  // budgetId === monthId for UC-008 (per API mapping note in file-storage.md rev 2).
  const budgetId = monthId;

  const [modalOpen, setModalOpen] = useState(false);
  const [lastSuccess, setLastSuccess] = useState<TaxDocumentUploadResponse | null>(null);
  const uploadMutation = useUploadTaxDocument();

  // Phase 22 stub state: backend authoritatively checks budget approval +
  // resolves tenant from JWT. Future enhancement (UC-005/UC-008 integration phase):
  // fetch budget detail + tax_document metadata via useBudgetDetail / useTaxDocumentMetadata hooks.
  const [budgetApproved] = useState(true);
  const [tenantId] = useState("00000000-0000-0000-0000-0000000000a1"); // placeholder seeded tenant
  const monthLabel = monthId;

  async function handleSubmit(values: TaxDocumentUploadFormValues) {
    try {
      const result = await uploadMutation.mutateAsync({ budgetId, tenantId, values });
      setLastSuccess(result);
      setModalOpen(false);
    } catch {
      // Server error stays in modal — uploadMutation.error surfaced via Alert in modal.
    }
  }

  const serverError =
    uploadMutation.error instanceof ApiError
      ? {
          error_code: uploadMutation.error.errorCode,
          message: uploadMutation.error.message,
        }
      : null;

  return (
    <section
      aria-labelledby="prefakturace-title"
      data-testid="prefakturace"
      className="max-w-7xl mx-auto space-y-6"
    >
      <header className="flex flex-col gap-1">
        <h2 id="prefakturace-title" className="text-display-xs font-bold text-primary m-0">
          Prefakturace A&amp;L soft — {monthLabel}
        </h2>
        <p className="text-md text-tertiary m-0">
          Nahrání daňového dokladu + automatické připojení vyúčtování + email Davidovi.
        </p>
      </header>

      {lastSuccess && (
        <InlineAlert variant="success" title="Doklad nahrán">
          Připojeno <strong>{lastSuccess.attachments_count}</strong>{" "}
          vyúčtování za {monthLabel} (v{lastSuccess.version_number}). David obdržel email.
          {lastSuccess.attachments_advisory && (
            <p className="mt-2 m-0">{lastSuccess.attachments_advisory}</p>
          )}
        </InlineAlert>
      )}

      <Card>
        <CardHeader title="Daňový doklad — distribuce" />
        <CardBody>
          <div className="space-y-4">
            <p data-testid="taxdoc-status" className="m-0 flex items-center gap-2">
              <span className="text-sm text-tertiary">Stav:</span>
              <BadgeWithDot color={lastSuccess ? "blue" : "warning"}>
                {lastSuccess ? `Nahrán (v${lastSuccess.version_number})` : "Nepřiřazen"}
              </BadgeWithDot>
            </p>
            {!budgetApproved && (
              <InlineAlert variant="warning" title="Rozpočet musí být schválen">
                Daňový doklad nelze nahrát před schválením rozpočtu. Vrať se na detail rozpočtu a schval ho.
              </InlineAlert>
            )}
            <Button
              color="primary"
              isDisabled={!budgetApproved}
              onPress={() => setModalOpen(true)}
              data-testid="taxdoc-open-upload"
            >
              {lastSuccess ? "Nahrát novou verzi" : "Nahrát doklad"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <TaxDocumentUploadModal
        open={modalOpen}
        budgetId={budgetId}
        monthLabel={monthLabel}
        tenantId={tenantId}
        budgetApproved={budgetApproved}
        existingVersion={lastSuccess?.version_number}
        submitting={uploadMutation.isPending}
        serverError={serverError}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </section>
  );
}
